import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  Grid,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  InputAdornment,
} from '@mui/material';
import { Edit, RestartAlt, Description, Search } from '@mui/icons-material';
import { MargemRelatorio, OrientacaoRelatorio } from '../types';
import { margemRelatorioService } from '../services/margemRelatorioService';
import { useAuth } from '../contexts/AuthContext';
import { getErrorMessage } from '../utils/errors';
import ConfirmDialog from '../components/ConfirmDialog';

const ORIENTACOES: OrientacaoRelatorio[] = ['RETRATO', 'PAISAGEM'];

const LABEL_ORIENTACAO: Record<OrientacaoRelatorio, string> = {
  RETRATO: 'Retrato (A4)',
  PAISAGEM: 'Paisagem (A4)',
};

const DIMENSOES: Record<OrientacaoRelatorio, { largura: number; altura: number }> = {
  RETRATO: { largura: 21, altura: 29.7 },
  PAISAGEM: { largura: 29.7, altura: 21 },
};

const CM_PARA_PT = 28.3465;
const CM_PARA_TWIPS = 566.929;
const MARGEM_MIN = 0;
const MARGEM_MAX = 10;

type MargemForm = {
  marginTop: string;
  marginBottom: string;
  marginLeft: string;
  marginRight: string;
};

type MargensForm = Record<OrientacaoRelatorio, MargemForm>;

const formVazio = (): MargensForm => ({
  RETRATO: { marginTop: '2', marginBottom: '2', marginLeft: '2', marginRight: '2' },
  PAISAGEM: { marginTop: '2', marginBottom: '2', marginLeft: '2', marginRight: '2' },
});

const toForm = (margens: MargemRelatorio[]): MargensForm => {
  const form = formVazio();
  margens.forEach((m) => {
    form[m.orientacao] = {
      marginTop: String(m.marginTop ?? 0),
      marginBottom: String(m.marginBottom ?? 0),
      marginLeft: String(m.marginLeft ?? 0),
      marginRight: String(m.marginRight ?? 0),
    };
  });
  return form;
};

const toPt = (cm: number) => Math.round((cm || 0) * CM_PARA_PT);

const toTwips = (cm: number) => Math.round((cm || 0) * CM_PARA_TWIPS);

const PreviewPagina: React.FC<{ orientacao: OrientacaoRelatorio; valores: MargemForm }> = ({
  orientacao,
  valores,
}) => {
  const { largura, altura } = DIMENSOES[orientacao];
  const num = (v: string) => Math.min(Math.max(Number(v) || 0, 0), MARGEM_MAX);

  return (
    <Box
      sx={{
        width: '100%',
        aspectRatio: `${largura} / ${altura}`,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.100',
        position: 'relative',
        borderRadius: 1,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: `${(num(valores.marginTop) / altura) * 100}%`,
          bottom: `${(num(valores.marginBottom) / altura) * 100}%`,
          left: `${(num(valores.marginLeft) / largura) * 100}%`,
          right: `${(num(valores.marginRight) / largura) * 100}%`,
          border: '1px dashed',
          borderColor: 'primary.main',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          conteúdo
        </Typography>
      </Box>
    </Box>
  );
};

const margemValida = (valor: string) => {
  const numero = Number(valor);
  return valor.trim() !== '' && !Number.isNaN(numero) && numero >= MARGEM_MIN && numero <= MARGEM_MAX;
};

const Margens: React.FC = () => {
  const [margens, setMargens] = useState<MargemRelatorio[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<MargensForm>(formVazio());
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [confirmReset, setConfirmReset] = useState<{ open: boolean; orientacao: OrientacaoRelatorio | null }>({
    open: false,
    orientacao: null,
  });
  const [filtro, setFiltro] = useState('');

  const { hasRole } = useAuth();
  const podeEditar = hasRole('ADMIN') || hasRole('GERENTE');

  useEffect(() => {
    loadMargens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMargens = async () => {
    try {
      const data = await margemRelatorioService.listar();
      setMargens(data);
      setForm(toForm(data));
    } catch (error) {
      setErro(getErrorMessage(error));
    }
  };

  const handleOpen = () => {
    setForm(toForm(margens));
    setErro('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setErro('');
  };

  const handleChange = (orientacao: OrientacaoRelatorio, campo: keyof MargemForm, valor: string) => {
    setForm((prev) => ({ ...prev, [orientacao]: { ...prev[orientacao], [campo]: valor } }));
  };

  const handleSave = async () => {
    const invalido = ORIENTACOES.some((orientacao) =>
      (Object.keys(form[orientacao]) as (keyof MargemForm)[]).some((campo) => !margemValida(form[orientacao][campo]))
    );
    if (invalido) {
      setErro(`As margens devem ser números entre ${MARGEM_MIN} e ${MARGEM_MAX} cm.`);
      return;
    }

    setSalvando(true);
    try {
      const payload: MargemRelatorio[] = ORIENTACOES.map((orientacao) => ({
        orientacao,
        marginTop: Number(form[orientacao].marginTop),
        marginBottom: Number(form[orientacao].marginBottom),
        marginLeft: Number(form[orientacao].marginLeft),
        marginRight: Number(form[orientacao].marginRight),
      }));
      await margemRelatorioService.salvar(payload);
      await loadMargens();
      handleClose();
    } catch (error) {
      setErro(getErrorMessage(error));
    } finally {
      setSalvando(false);
    }
  };

  const confirmResetAction = async () => {
    if (!confirmReset.orientacao) return;
    try {
      await margemRelatorioService.restaurarPadrao(confirmReset.orientacao);
      await loadMargens();
    } catch (error) {
      setErro(getErrorMessage(error));
    } finally {
      setConfirmReset({ open: false, orientacao: null });
    }
  };

  const getMargem = (orientacao: OrientacaoRelatorio) =>
    margens.find((m) => m.orientacao === orientacao);

  const camposMargem: { campo: keyof MargemForm; label: string }[] = [
    { campo: 'marginTop', label: 'Topo (cm)' },
    { campo: 'marginBottom', label: 'Base (cm)' },
    { campo: 'marginLeft', label: 'Esquerda (cm)' },
    { campo: 'marginRight', label: 'Direita (cm)' },
  ];

  const termo = filtro.trim().toLowerCase();
  const margensFiltradas = termo
    ? margens.filter((item) => Object.values(item).filter(Boolean).some((campo) => String(campo).toLowerCase().includes(termo)))
    : margens;

  return (
    <Container disableGutters maxWidth={false}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4">Margens de Relatório</Typography>
          <Typography variant="body2" color="text.secondary">
            Configuração das margens (em cm) usadas na geração de relatórios em PDF.
          </Typography>
        </Box>
        <Box display="flex" gap={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Pesquisar"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ minWidth: 260 }}
          />
          {podeEditar && (
            <Button variant="contained" startIcon={<Edit />} onClick={handleOpen}>
              Configurar Margens
            </Button>
          )}
        </Box>
      </Box>

      {erro && !open && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Orientação</TableCell>
              <TableCell>Topo</TableCell>
              <TableCell>Base</TableCell>
              <TableCell>Esquerda</TableCell>
              <TableCell>Direita</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ORIENTACOES.map((orientacao) => {
              const margem = getMargem(orientacao);
              const valores = [
                margem?.marginTop ?? 0,
                margem?.marginBottom ?? 0,
                margem?.marginLeft ?? 0,
                margem?.marginRight ?? 0,
              ];
              return (
                <TableRow key={orientacao}>
                  <TableCell>{LABEL_ORIENTACAO[orientacao]}</TableCell>
                  {valores.map((valor, index) => (
                    <TableCell key={index}>
                      {Number(valor).toFixed(1)} cm
                      <Typography variant="caption" color="text.secondary" display="block">
                        {toPt(Number(valor))} pt
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {toTwips(Number(valor))} twips
                      </Typography>
                    </TableCell>
                  ))}
                  <TableCell align="right">
                    {hasRole('ADMIN') && (
                      <Tooltip title="Restaurar padrão">
                        <IconButton
                          size="small"
                          onClick={() => setConfirmReset({ open: true, orientacao })}
                        >
                          <RestartAlt />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {margens.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    Nenhuma margem configurada. O padrão de 2.0 cm é utilizado.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Description fontSize="small" />
            Configurar Margens de Relatório
          </Box>
        </DialogTitle>
        <DialogContent>
          {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
          <Grid container spacing={3} sx={{ mt: 0 }}>
            {ORIENTACOES.map((orientacao) => (
              <Grid item xs={12} md={6} key={orientacao}>
                <Typography variant="subtitle1" gutterBottom>
                  {LABEL_ORIENTACAO[orientacao]}
                </Typography>
                <Grid container spacing={2}>
                  {camposMargem.map(({ campo, label }) => (
                    <Grid item xs={6} key={campo}>
                      <TextField
                        fullWidth
                        label={label}
                        type="number"
                        value={form[orientacao][campo]}
                        onChange={(e) => handleChange(orientacao, campo, e.target.value)}
                        inputProps={{ step: 0.1, min: MARGEM_MIN, max: MARGEM_MAX }}
                        helperText={`${toPt(Number(form[orientacao][campo]) || 0)} pt · ${toTwips(Number(form[orientacao][campo]) || 0)} twips`}
                      />
                    </Grid>
                  ))}
                </Grid>
                <Box sx={{ mt: 2, maxWidth: 200, mx: 'auto' }}>
                  <PreviewPagina orientacao={orientacao} valores={form[orientacao]} />
                </Box>
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ mt: 3 }} />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Valores em centímetros (0 a {MARGEM_MAX} cm). A pré-visualização é apenas ilustrativa.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={salvando}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmReset.open}
        title="Restaurar margens padrão"
        message="Tem certeza que deseja restaurar as margens desta orientação para o padrão de 2.0 cm?"
        confirmText="Restaurar"
        severity="warning"
        onConfirm={confirmResetAction}
        onCancel={() => setConfirmReset({ open: false, orientacao: null })}
      />
    </Container>
  );
};

export default Margens;
